import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import { useParams, Link } from "react-router-dom";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Buildings, Plus, PencilSimple, TrashSimple, MagnifyingGlass,
  Phone, EnvelopeSimple, MapPin, Bank, X, UsersThree, IdentificationBadge,
  Globe, User, Briefcase, Note, Star, FilePdf, ArrowLeft,
  CaretUp, CaretDown, ArrowsDownUp,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";

const emptyForm = {
  kind: "alt_yuklenici",
  team_id: "",
  name: "",
  trade_name: "",
  tax_office: "",
  tax_number: "",
  contact_person: "",
  contact_title: "",
  phone: "",
  mobile: "",
  email: "",
  website: "",
  address: "",
  city: "",
  district: "",
  bank_name: "",
  bank_branch: "",
  iban: "",
  account_holder: "",
  specialties: [],
  site_ids: [],
  status: "aktif",
  notes: "",
};

const STATUS_META = {
  aktif: { label: "Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pasif: { label: "Pasif", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  kara_liste: { label: "Kara Liste", cls: "bg-rose-100 text-rose-700 border-rose-300" },
};

const KIND_META = {
  alt_yuklenici: {
    label: "Alt Yüklenici",
    shortLabel: "Alt Yüklenici",
    icon: Buildings,
    cls: "bg-[#1B4776] text-white border-[#1B4776]",
    softCls: "bg-blue-50 text-[#1B4776] border-blue-200",
  },
  taseron: {
    label: "Taşeron (Ekip)",
    shortLabel: "Taşeron",
    icon: UsersThree,
    cls: "bg-[#95C11F] text-white border-[#95C11F]",
    softCls: "bg-[#F7FBE9] text-[#556B10] border-[#95C11F]",
  },
};

export default function SubFirms() {
  const { sites } = useSites();
  const { siteId } = useParams();
  const currentSite = useMemo(
    () => (sites || []).find((s) => s.id === siteId),
    [sites, siteId],
  );
  const isSiteScoped = !!siteId;

  const [rows, setRows] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [specialtyFilter, setSpecialtyFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [form, setForm] = useState(emptyForm);
  const [specialtyInput, setSpecialtyInput] = useState("");
  const confirm = useConfirm();

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (kindFilter !== "all") params.kind = kindFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (specialtyFilter !== "all") params.specialty = specialtyFilter;
      // Şantiye bazlı görüntülemedeyken URL'deki siteId'yi zorunlu filtre olarak uygula
      if (isSiteScoped) params.site_id = siteId;
      else if (siteFilter !== "all") params.site_id = siteFilter;
      const { data } = await axios.get(`${API}/subcontractors`, { params });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadSpecialties = async () => {
    try {
      const { data } = await axios.get(`${API}/subcontractors/specialties`);
      setSpecialties(data);
    } catch (e) { /* ignore */ }
  };
  const loadAvailableTeams = async (includeId = "") => {
    try {
      const params = { exclude_registered: true };
      if (includeId) params.include_id = includeId;
      const { data } = await axios.get(`${API}/subcontractors/available-teams`, { params });
      setAvailableTeams(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, [q, kindFilter, statusFilter, specialtyFilter, siteFilter, siteId]); // eslint-disable-line
  useEffect(() => { loadSpecialties(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      site_ids: isSiteScoped ? [siteId] : [],
    });
    setSpecialtyInput("");
    loadAvailableTeams("");
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      kind: r.kind || "alt_yuklenici",
      team_id: r.team_id || "",
      name: r.name || "",
      trade_name: r.trade_name || "",
      tax_office: r.tax_office || "",
      tax_number: r.tax_number || "",
      contact_person: r.contact_person || "",
      contact_title: r.contact_title || "",
      phone: r.phone || "",
      mobile: r.mobile || "",
      email: r.email || "",
      website: r.website || "",
      address: r.address || "",
      city: r.city || "",
      district: r.district || "",
      bank_name: r.bank_name || "",
      bank_branch: r.bank_branch || "",
      iban: r.iban || "",
      account_holder: r.account_holder || "",
      specialties: r.specialties || [],
      site_ids: r.site_ids || [],
      status: r.status || "aktif",
      notes: r.notes || "",
    });
    setSpecialtyInput("");
    loadAvailableTeams(r.id);
    setOpen(true);
  };

  // Taşeron için ekip seçildiğinde ismi otomatik doldur
  const onSelectTeam = (teamId) => {
    const t = availableTeams.find((x) => x.id === teamId);
    setForm((f) => {
      const nextSites = t?.site_id && !(f.site_ids || []).includes(t.site_id)
        ? [...(f.site_ids || []), t.site_id]
        : (f.site_ids || []);
      return {
        ...f,
        team_id: teamId,
        name: (t?.name) || f.name,
        contact_person: t?.leader_name || f.contact_person,
        mobile: t?.leader_phone || f.mobile,
        site_ids: nextSites,
      };
    });
  };

  const toggleSite = (sid) => setForm((f) => ({
    ...f,
    site_ids: (f.site_ids || []).includes(sid)
      ? f.site_ids.filter((x) => x !== sid)
      : [...(f.site_ids || []), sid],
  }));

  const addSpecialty = () => {
    const v = specialtyInput.trim();
    if (!v) return;
    if (form.specialties.includes(v)) { toast.info("Bu uzmanlık zaten eklenmiş"); return; }
    set("specialties", [...form.specialties, v]);
    setSpecialtyInput("");
  };
  const removeSpecialty = (v) => set("specialties", form.specialties.filter((s) => s !== v));

  const save = async () => {
    try {
      if (form.kind === "taseron" && !form.team_id) {
        toast.error("Taşeron için ekip seçmelisiniz"); return;
      }
      if (!form.name.trim() && form.kind !== "taseron") {
        toast.error("Firma adı zorunludur"); return;
      }
      if (editing) {
        await axios.put(`${API}/subcontractors/${editing.id}`, form);
        toast.success("Kayıt güncellendi");
      } else {
        await axios.post(`${API}/subcontractors`, form);
        toast.success("Kayıt eklendi");
      }
      setOpen(false);
      load(); loadSpecialties();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: "Kaydı Sil",
      description: `${r.name} silinsin mi?`,
      confirmText: "Sil", variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/subcontractors/${r.id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const stats = useMemo(() => ({
    total: rows.length,
    altYuklenici: rows.filter((r) => (r.kind || "alt_yuklenici") === "alt_yuklenici").length,
    taseron: rows.filter((r) => r.kind === "taseron").length,
    aktif: rows.filter((r) => r.status === "aktif").length,
    karaListe: rows.filter((r) => r.status === "kara_liste").length,
  }), [rows]);

  const norm = (s) => (s || "").toString().trim().toLowerCase()
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
    .replace(/İ/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u");
  const toggleSort = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
  );
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const getVal = (r) => {
      const k = r.kind || "alt_yuklenici";
      const isT = k === "taseron";
      const contact = r.contact_person || (isT ? r.team_leader_name : "") || "";
      switch (sort.key) {
        case "kind":    return norm(k === "taseron" ? "1_taseron" : "2_altyuklenici");
        case "name":    return norm(r.name);
        case "tax":     return norm(r.tax_number);
        case "contact": return norm(contact);
        case "trade":   return norm(r.trade_name || r.specialty);
        case "site":    return norm((r.site_names || []).join(", "));
        case "status":  return norm(r.status);
        default:        return norm(r.name);
      }
    };
    arr.sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sort]);

  const isTaseron = form.kind === "taseron";

  return (
    <Shell
      title={currentSite ? `${currentSite.name} — Firma/Ekip Bilgileri` : "Firma/Ekip Bilgileri"}
      subtitle={isSiteScoped ? "Alt Yüklenici Yönetimi › Şantiye Bazlı" : "Alt Yüklenici Yönetimi"}
      hideSiteSelector={isSiteScoped}
    >
      {isSiteScoped && (
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2 text-[11px] text-slate-600">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Şantiye:</span>
            {currentSite ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200">
                <span className="font-mono text-[10px] font-bold text-[#1B4776]">{currentSite.code}</span>
                <span className="text-slate-700">{currentSite.name}</span>
              </span>
            ) : <span className="text-slate-400">Bulunamadı</span>}
          </div>
          <Link to="/taseron/firmalar">
            <Button variant="outline" data-testid="sub-firms-back"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <ArrowLeft size={14} className="mr-1.5" /> Şantiye Listesi
            </Button>
          </Link>
        </div>
      )}
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Toplam Kayıt</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1">{stats.total}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-blue-50/50">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1B4776] font-bold">Alt Yüklenici</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1">{stats.altYuklenici}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Resmi firmalar (VKN'li)</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 bg-[#F7FBE9]/60">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#556B10] font-bold">Taşeron (Ekip)</div>
          <div className="text-2xl font-bold text-[#556B10] mt-1">{stats.taseron}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Ekip bazlı çalışanlar</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Aktif</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.aktif}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-rose-700 font-bold">Kara Liste</div>
          <div className="text-2xl font-bold text-rose-700 mt-1">{stats.karaListe}</div>
        </Card>
      </div>

      {/* Toolbar */}
      <Card className="p-4 mb-3 rounded-none sm:rounded-sm border-slate-200">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-3 flex-1 items-center">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="sub-search"
                     placeholder="Firma / ekip adı, VKN, iletişim..."
                     value={q}
                     onChange={(e) => setQ(e.target.value)}
                     className="pl-9 rounded-none sm:rounded-sm" />
            </div>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-44 rounded-none sm:rounded-sm" data-testid="sub-kind-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü (A.Y. + Taşeron)</SelectItem>
                <SelectItem value="alt_yuklenici">Alt Yüklenici</SelectItem>
                <SelectItem value="taseron">Taşeron (Ekip)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 rounded-none sm:rounded-sm" data-testid="sub-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="aktif">Aktif</SelectItem>
                <SelectItem value="pasif">Pasif</SelectItem>
                <SelectItem value="kara_liste">Kara Liste</SelectItem>
              </SelectContent>
            </Select>
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className={`w-44 rounded-none sm:rounded-sm ${isSiteScoped ? "hidden" : ""}`} data-testid="sub-site-filter">
                <SelectValue placeholder="Şantiye" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şantiyeler</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="font-mono text-xs text-[#1B4776] mr-2">{s.code}</span>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => {
              const url = new URL(`${API}/subcontractors/export.pdf`);
              // Site-scoped moddayken URL param'ındaki siteId zorunlu; aksi hâlde dropdown filtresi
              const effectiveSite = isSiteScoped ? siteId : (siteFilter !== "all" ? siteFilter : "");
              if (effectiveSite) url.searchParams.set("site_id", effectiveSite);
              if (kindFilter && kindFilter !== "all") url.searchParams.set("kind", kindFilter);
              if (statusFilter && statusFilter !== "all") url.searchParams.set("status", statusFilter);
              if (sort?.key) url.searchParams.set("sort_by", sort.key);
              if (sort?.dir) url.searchParams.set("sort_dir", sort.dir);
              window.open(url.toString(), "_blank", "noopener,noreferrer");
            }}
            data-testid="sub-export-pdf"
            variant="outline"
            className="rounded-none sm:rounded-sm border-[#1B4776] text-[#1B4776] hover:bg-[#1B4776] hover:text-white"
          >
            <FilePdf size={14} className="mr-1.5" /> PDF İndir
          </Button>
          <Button onClick={openNew}
                  className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm"
                  data-testid="sub-add-btn">
            <Plus size={14} weight="bold" className="mr-1.5" />
            Yeni Kayıt
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <ThSort label="Tür" k="kind" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-32" />
                <ThSort label="Firma / Ekip Adı" k="name" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left" />
                <ThSort label="VKN / TCKN" k="tax" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-36" />
                <ThSort label="İlgili Kişi" k="contact" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-44" />
                <ThSort label="Görev / Uzmanlık" k="trade" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-56" />
                <ThSort label="Atandığı Şantiye" k="site" sort={sort} onSort={toggleSort} className="px-4 py-3 text-left w-40" />
                <ThSort label="Durum" k="status" sort={sort} onSort={toggleSort} className="px-4 py-3 text-center w-24" align="center" />
                <th className="px-4 py-3 text-right w-32">İşlem</th>
              </tr>
            </thead>
            <tbody data-testid="sub-table-body">
              {sortedRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                    <Buildings size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">Henüz kayıt yok — "Yeni Kayıt" ile başlayın.</p>
                  </td>
                </tr>
              )}
              {sortedRows.map((r, i) => {
                const kind = r.kind || "alt_yuklenici";
                const KindIcon = KIND_META[kind]?.icon || Buildings;
                const isT = kind === "taseron";
                const contact = r.contact_person || (isT ? r.team_leader_name : "") || "";
                const contactPhone = r.mobile || r.phone || (isT ? r.team_leader_phone : "") || "";
                return (
                  <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${
                    isT ? "bg-[#F7FBE9]/30" : (i % 2 ? "bg-slate-50/50" : "")
                  }`} data-testid={`sub-row-${i}`}>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${KIND_META[kind]?.softCls}`}>
                        <KindIcon size={10} weight="bold" />
                        {KIND_META[kind]?.shortLabel}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-slate-800">{r.name}</div>
                      {isT && r.team_code && (
                        <div className="text-[10px] text-[#556B10] font-mono font-bold mt-0.5">Ekip Kodu: {r.team_code}</div>
                      )}
                      {!isT && r.trade_name && <div className="text-[10px] text-slate-500">{r.trade_name}</div>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">
                      {r.tax_number ? (
                        <div>
                          <div>{r.tax_number}</div>
                          <div className="text-[9px] text-slate-400 mt-0.5 uppercase tracking-wider">
                            {isT ? "TCKN" : "VKN"}
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {contact ? (
                        <div className="font-semibold text-slate-700">{contact}</div>
                      ) : <span className="text-slate-300">—</span>}
                      {contactPhone && (
                        <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5">
                          <Phone size={9} weight="bold" /> {contactPhone}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {isT && r.team_duty && (
                        <div className="text-xs font-semibold text-[#556B10] mb-1">{r.team_duty}</div>
                      )}
                      <div className="flex flex-wrap gap-1">
                        {(r.specialties || []).slice(0, 3).map((s) => (
                          <span key={s} className="inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-slate-100 text-slate-700 border border-slate-300">
                            {s}
                          </span>
                        ))}
                        {(r.specialties || []).length > 3 && (
                          <span className="text-[9px] text-slate-400 self-center">+{r.specialties.length - 3}</span>
                        )}
                        {!isT && (r.specialties || []).length === 0 && !r.team_duty && (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {(r.sites || []).length === 0 ? (
                        <span className="text-slate-300 text-xs">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(r.sites || []).slice(0, 3).map((s) => (
                            <span key={s.id}
                                  className="inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-blue-50 text-[#1B4776] border border-blue-200"
                                  title={s.name}>
                              {s.code || s.name}
                            </span>
                          ))}
                          {(r.sites || []).length > 3 && (
                            <span className="text-[9px] text-slate-400 self-center">+{r.sites.length - 3}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-flex items-center text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${STATUS_META[r.status]?.cls || STATUS_META.aktif.cls}`}>
                        {STATUS_META[r.status]?.label || r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => openEdit(r)} className="p-1 text-slate-400 hover:text-[#1B4776]"
                                title="Düzenle" data-testid={`sub-edit-${i}`}>
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button onClick={() => remove(r)} className="p-1 text-slate-400 hover:text-rose-600"
                                title="Sil" data-testid={`sub-del-${i}`}>
                          <TrashSimple size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-none sm:rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Buildings size={18} weight="duotone" className="text-[#95C11F]" />
              {editing ? `Kayıt Düzenle: ${editing.name}` : "Yeni Kayıt"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Kayıt Türü Seçici */}
            <div className="p-3 border-2 border-slate-200 bg-slate-50">
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">
                Kayıt Türü *
              </label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button type="button"
                        onClick={() => setForm((f) => ({ ...f, kind: "alt_yuklenici", team_id: "" }))}
                        disabled={!!editing}
                        className={`p-3 border-2 text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                          form.kind === "alt_yuklenici"
                            ? "border-[#1B4776] bg-white"
                            : "border-slate-200 bg-white hover:border-[#1B4776]/60"
                        }`}
                        data-testid="sub-kind-alt-yuklenici">
                  <div className="flex items-center gap-2">
                    <Buildings size={16} weight="duotone" className="text-[#1B4776]" />
                    <div className="text-sm font-bold text-[#1B4776]">Alt Yüklenici</div>
                  </div>
                  <div className="text-[10px] text-slate-600 mt-1">Resmi firma — VKN, IBAN, iletişim bilgileri girilir</div>
                </button>
                <button type="button"
                        onClick={() => setForm((f) => ({ ...f, kind: "taseron" }))}
                        disabled={!!editing}
                        className={`p-3 border-2 text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                          form.kind === "taseron"
                            ? "border-[#95C11F] bg-white"
                            : "border-slate-200 bg-white hover:border-[#95C11F]/60"
                        }`}
                        data-testid="sub-kind-taseron">
                  <div className="flex items-center gap-2">
                    <UsersThree size={16} weight="duotone" className="text-[#556B10]" />
                    <div className="text-sm font-bold text-[#556B10]">Taşeron (Ekip)</div>
                  </div>
                  <div className="text-[10px] text-slate-600 mt-1">Resmi olmayan — Ekip Listesi'nden ekip seçilir</div>
                </button>
              </div>
              {editing && (
                <div className="text-[10px] text-slate-400 mt-2">
                  Kayıt türü mevcut kayıtlarda değiştirilemez.
                </div>
              )}
            </div>

            {/* Taşeron ise ekip seçici */}
            {isTaseron && (
              <div className="p-3 border-2 border-[#95C11F] bg-[#F7FBE9]/50">
                <label className="text-[10px] uppercase tracking-widest font-bold text-[#556B10]">
                  Ekip Seçimi * <span className="text-slate-400 normal-case">(Ekip Listesi'nden)</span>
                </label>
                <Select value={form.team_id} onValueChange={onSelectTeam}>
                  <SelectTrigger className="rounded-none sm:rounded-sm bg-white mt-1" data-testid="sub-team-select">
                    <SelectValue placeholder="— Ekip seçin —" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTeams.length === 0 && (
                      <div className="px-2 py-4 text-xs text-slate-500 text-center">
                        Uygun ekip yok (hepsi taşeron olarak kaydedilmiş)
                      </div>
                    )}
                    {availableTeams.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px] text-[#556B10] bg-[#F7FBE9] border border-[#95C11F] px-1">
                            {t.code}
                          </span>
                          <span className="font-semibold">{t.name}</span>
                          {t.duty && <span className="text-slate-400 text-xs">— {t.duty}</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.team_id && (
                  <div className="mt-2 text-[10px] text-slate-600">
                    <IdentificationBadge size={10} weight="bold" className="inline mr-1" />
                    Ekip bilgileri (isim, lider, telefon) otomatik olarak dolduruldu.
                  </div>
                )}
              </div>
            )}

            {/* Firma Bilgileri — Alt Yüklenici için detaylı, Taşeron için opsiyonel */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] mb-2 pb-1 border-b border-[#1B4776]/20">
                {isTaseron ? "Firma / Ekip Bilgileri (Opsiyonel)" : "Firma Bilgileri"}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    {isTaseron ? "Ekip Adı" : "Ticaret Ünvanı *"}
                  </label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                         placeholder={isTaseron ? "Ekip adı (otomatik dolar)" : "Örn: Yılmaz İnşaat Taahhüt Ltd. Şti."}
                         className="rounded-none sm:rounded-sm" data-testid="sub-name" />
                </div>
                {!isTaseron && (
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kısa Ad</label>
                    <Input value={form.trade_name} onChange={(e) => set("trade_name", e.target.value)}
                           placeholder="Yılmaz İnşaat" className="rounded-none sm:rounded-sm" data-testid="sub-trade" />
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Durum</label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="sub-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aktif">Aktif</SelectItem>
                      <SelectItem value="pasif">Pasif</SelectItem>
                      <SelectItem value="kara_liste">Kara Liste</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!isTaseron && (
                  <>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Vergi Dairesi</label>
                      <Input value={form.tax_office} onChange={(e) => set("tax_office", e.target.value)}
                             className="rounded-none sm:rounded-sm" data-testid="sub-tax-office" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Vergi No / VKN</label>
                      <Input value={form.tax_number} onChange={(e) => set("tax_number", e.target.value)}
                             placeholder="1234567890"
                             className="rounded-none sm:rounded-sm font-mono" data-testid="sub-tax-no" />
                    </div>
                  </>
                )}
                {isTaseron && (
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                      T.C. Kimlik Numarası (TCKN)
                    </label>
                    <Input value={form.tax_number}
                           onChange={(e) => set("tax_number", e.target.value.replace(/\D/g, "").slice(0, 11))}
                           placeholder="11 haneli TC Kimlik No"
                           maxLength={11}
                           className="rounded-none sm:rounded-sm font-mono" data-testid="sub-tckn" />
                    <div className="text-[10px] text-slate-400 mt-0.5">Ekip liderinin / taşeron kişinin TCKN'si</div>
                  </div>
                )}
              </div>
            </div>

            {/* İletişim */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] mb-2 pb-1 border-b border-[#1B4776]/20">
                İletişim
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    {isTaseron ? "Ekip Lideri" : "İletişim Kişisi"}
                  </label>
                  <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)}
                         placeholder="Ad Soyad" className="rounded-none sm:rounded-sm" data-testid="sub-contact-person" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Ünvanı</label>
                  <Input value={form.contact_title} onChange={(e) => set("contact_title", e.target.value)}
                         placeholder={isTaseron ? "Reis / Formen" : "Şantiye Şefi"}
                         className="rounded-none sm:rounded-sm" data-testid="sub-contact-title" />
                </div>
                {!isTaseron && (
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Firma Telefonu</label>
                    <Input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                           placeholder="0216 555 12 34" className="rounded-none sm:rounded-sm" data-testid="sub-phone" />
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">GSM (Yetkili)</label>
                  <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)}
                         placeholder="0532 555 12 34" className="rounded-none sm:rounded-sm" data-testid="sub-mobile" />
                </div>
                {!isTaseron && (
                  <>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">E-posta</label>
                      <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                             placeholder="info@firma.com" className="rounded-none sm:rounded-sm" data-testid="sub-email" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Web Sitesi</label>
                      <Input value={form.website} onChange={(e) => set("website", e.target.value)}
                             placeholder="https://" className="rounded-none sm:rounded-sm" data-testid="sub-website" />
                    </div>
                  </>
                )}
                {!isTaseron && (
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Adres</label>
                    <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)}
                              className="rounded-none sm:rounded-sm" data-testid="sub-address" />
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Şehir</label>
                  <Input value={form.city} onChange={(e) => set("city", e.target.value)}
                         className="rounded-none sm:rounded-sm" data-testid="sub-city" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">İlçe</label>
                  <Input value={form.district} onChange={(e) => set("district", e.target.value)}
                         className="rounded-none sm:rounded-sm" data-testid="sub-district" />
                </div>
              </div>
            </div>

            {/* Şantiye Eşleştirme */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] mb-2 pb-1 border-b border-[#1B4776]/20">
                Şantiye Eşleştirmesi
                <span className="text-slate-400 normal-case tracking-normal ml-2 font-normal">
                  (Bu kayıt hangi şantiye(ler)de çalışıyor?)
                </span>
              </div>
              {sites.length === 0 ? (
                <div className="text-xs text-slate-400 italic">Henüz şantiye tanımlı değil.</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {sites.map((s) => {
                    const active = (form.site_ids || []).includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleSite(s.id)}
                        data-testid={`sub-site-toggle-${s.code || s.id}`}
                        className={`text-left p-2 border-2 transition-colors ${
                          active
                            ? "border-[#1B4776] bg-blue-50"
                            : "border-slate-200 bg-white hover:border-[#1B4776]/40"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 border-2 flex items-center justify-center ${
                            active ? "border-[#1B4776] bg-[#1B4776]" : "border-slate-300"
                          }`}>
                            {active && <span className="text-white text-[10px] font-bold">✓</span>}
                          </div>
                          <div>
                            <div className="text-xs font-bold text-[#1B4776]">{s.code}</div>
                            <div className="text-[11px] text-slate-700">{s.name}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {isTaseron && form.team_id && (
                <div className="text-[10px] text-slate-500 mt-2">
                  Ekip seçildiğinde bağlı şantiyesi otomatik eklendi — istediğinizi kaldırıp ekleyebilirsiniz.
                </div>
              )}
            </div>

            {/* Banka — sadece Alt Yüklenici için */}
            {!isTaseron && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] mb-2 pb-1 border-b border-[#1B4776]/20">
                  Banka Bilgileri
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Banka</label>
                    <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)}
                           placeholder="Ziraat Bankası" className="rounded-none sm:rounded-sm" data-testid="sub-bank-name" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Şube</label>
                    <Input value={form.bank_branch} onChange={(e) => set("bank_branch", e.target.value)}
                           className="rounded-none sm:rounded-sm" data-testid="sub-bank-branch" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">IBAN</label>
                    <Input value={form.iban}
                           onChange={(e) => set("iban", e.target.value.toUpperCase())}
                           placeholder="TR00 0000 0000 0000 0000 0000 00"
                           className="rounded-none sm:rounded-sm font-mono" data-testid="sub-iban" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Hesap Sahibi</label>
                    <Input value={form.account_holder} onChange={(e) => set("account_holder", e.target.value)}
                           placeholder="Firmanın adı ile aynıysa boş bırakabilirsiniz"
                           className="rounded-none sm:rounded-sm" data-testid="sub-account-holder" />
                  </div>
                </div>
              </div>
            )}

            {/* Notlar */}
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Notlar</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                        className="rounded-none sm:rounded-sm" data-testid="sub-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={save}
                    className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm"
                    data-testid="sub-save">
              {editing ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Shell>
  );
}

/** Kart bölüm başlığı + grid çerçevesi */
function SubCardSection({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#1B4776] mb-2 pb-1 border-b border-[#1B4776]/20">
        {Icon && <Icon size={12} weight="bold" />}
        {title}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {children}
      </div>
    </div>
  );
}

function ThSort({ label, k, sort, onSort, className = "", align = "left" }) {
  const active = sort.key === k;
  const dir = active ? sort.dir : null;
  return (
    <th className={className}>
      <button
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.2em] font-bold ${
          active ? "text-[#1B4776]" : "text-slate-500"
        } hover:text-[#1B4776]`}
        style={{ justifyContent: align === "center" ? "center" : "flex-start", width: "100%" }}
      >
        {label}
        {dir === "asc" && <CaretUp size={10} weight="bold" />}
        {dir === "desc" && <CaretDown size={10} weight="bold" />}
        {!active && <ArrowsDownUp size={10} weight="bold" className="opacity-30" />}
      </button>
    </th>
  );
}

